import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { RootState } from '../rootReducer'
import { ReadCPxCardResponse, ReadVitaleCardResponse, StartCpxCardMonitoringResponse, StartVitaleCardMonitoringResponse } from '@/core/types/dmp-connect'

type DmpConnectReducerState = {
  connectorOk: boolean
  connectorConfig: {
    dcParameter?: string
  }
  monitoring: {
    cpxCard: StartCpxCardMonitoringResponse | null
    vitaleCard: StartVitaleCardMonitoringResponse | null
  }
  session: {
    sessionId?: string
  }
  tlsiConnector: {
    billingNumber: string
    instanceUniqueId: string
    practiceSetting: string
  } | null
  cpxCard: (ReadCPxCardResponse & { encodedPinCode: string }) | null
  cpxCardPracticeLocationIndex: number | null
  cpxCardReaderName: string | null
  vitaleCard: ReadVitaleCardResponse | null
  vitaleCardReaderName: string | null
}

const dmpConnectPersistConfig = {
  key: 'promotal.medconnect_dmpConnect',
  storage,
  whitelist: ['connectorConfig', 'session', 'cpxCard', 'cpxCardPracticeLocationIndex', 'cpxCardReaderName', 'tlsiConnector'],
}

const initialState: DmpConnectReducerState = {
  connectorOk: true,
  connectorConfig: {},
  monitoring: {
    cpxCard: null,
    vitaleCard: null,
  },
  session: {},
  tlsiConnector: null,
  cpxCard: null,
  cpxCardPracticeLocationIndex: null,
  cpxCardReaderName: null,
  vitaleCard: null,
  vitaleCardReaderName: null,
}

const dmpConnectSlice = createSlice({
  name: 'dmpConnect',
  initialState,
  reducers: {
    setConnectorOk(state, action: PayloadAction<boolean>) {
      state.connectorOk = action.payload
    },
    setCpxCard(state, action: PayloadAction<(ReadCPxCardResponse & { encodedPinCode: string }) | null>) {
      state.cpxCard = action.payload
    },
    setCpxCardMonitoringData(state, action: PayloadAction<StartCpxCardMonitoringResponse | null>) {
      state.monitoring.cpxCard = action.payload
    },
    setCpxCardPracticeLocationIndex(state, action: PayloadAction<number | null>) {
      state.cpxCardPracticeLocationIndex = action.payload
    },
    setCpxCardReaderName(state, action: PayloadAction<string | null>) {
      state.cpxCardReaderName = action.payload
    },
    setDcParameter(state, action: PayloadAction<string | undefined>) {
      state.connectorConfig.dcParameter = action.payload
    },
    setSessionId(state, action: PayloadAction<string>) {
      state.session.sessionId = action.payload
    },
    resetSession(state) {
      state.cpxCard = null
      state.session.sessionId = undefined
      state.tlsiConnector = null
    },
    setTlsiConnector(state, action: PayloadAction<DmpConnectReducerState['tlsiConnector']>) {
      state.tlsiConnector = action.payload
    },
    setVitaleCardMonitoringData(state, action: PayloadAction<StartVitaleCardMonitoringResponse>) {
      state.monitoring.vitaleCard = action.payload
    },
    setVitaleCard(state, action: PayloadAction<ReadVitaleCardResponse | null>) {
      state.vitaleCard = action.payload
    },
    setVitaleCardReaderName(state, action: PayloadAction<string | null>) {
      state.vitaleCardReaderName = action.payload
    },
  },
})

export const selectDMPConnectOk = (state: RootState) => state.dmpConnect.connectorOk
export const selectDMPConnectSessionId = (state: RootState) => state.dmpConnect.session.sessionId
export const selectDMPConnectDcParameter = (state: RootState) => state.dmpConnect.connectorConfig.dcParameter
export const selectDMPConnectTlsiConnector = (state: RootState) => state.dmpConnect.tlsiConnector
export const selectDMPConnectTlsiConnectorId = (state: RootState) => state.dmpConnect.tlsiConnector?.instanceUniqueId
export const selectDMPConnectTlsiConnectorBillingNumber = (state: RootState) => state.dmpConnect.tlsiConnector?.billingNumber
export const selectCpxCard = (state: RootState) => state.dmpConnect.cpxCard
export const selectCpxCardPracticeLocationIndex = (state: RootState) => state.dmpConnect.cpxCardPracticeLocationIndex
export const selectCpxCardReaderName = (state: RootState) => state.dmpConnect.cpxCardReaderName
export const selectCPxCardMonitoringData = (state: RootState) => state.dmpConnect.monitoring.cpxCard
export const selectPracticeLocations = (state: RootState) => state.dmpConnect.cpxCard?.PracticeLocations
export const selectDMPConnectVitaleCardMonitoring = (state: RootState) => state.dmpConnect.monitoring.vitaleCard
export const selectVitaleCard = (state: RootState) => state.dmpConnect.vitaleCard
export const selectVitaleCardReaderName = (state: RootState) => state.dmpConnect.vitaleCardReaderName

export const {
  setConnectorOk,
  setCpxCard,
  setCpxCardMonitoringData,
  setCpxCardPracticeLocationIndex,
  setCpxCardReaderName,
  setDcParameter,
  setSessionId,
  resetSession,
  setTlsiConnector,
  setVitaleCardMonitoringData,
  setVitaleCard,
  setVitaleCardReaderName,
} = dmpConnectSlice.actions

export default persistReducer(dmpConnectPersistConfig, dmpConnectSlice.reducer)
